// src/layouts/DashboardLayout.tsx
import { Outlet, Link, useLocation, useNavigate } from 'react-router';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, Car, PlusCircle, Users, MessageSquare,
  CreditCard, BarChart2, Settings, Menu, X, Search,
  ChevronDown, LogOut, User, Building2, Tag, FileText, Wallet, AlertTriangle
} from 'lucide-react';
import logo from '../../imports/logo-MORROCARS.png';
import Cookies from "js-cookie";
import ScrollToTop from "../components/ScrollToTop";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { NotificationBell } from '../components/NotificationBell';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}
const isLocalhost = window.location.hostname === 'localhost';

const cookieOptions: Cookies.CookieAttributes = {
  path: '/',
  domain: isLocalhost ? undefined : '.morrocars.com',
  secure: !isLocalhost,
  sameSite: isLocalhost ? 'Lax' : 'None',
};

export function DashboardLayout() {
  const { t } = useTranslation('auth');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [userData, setUserData] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    customerType: number;
    profilePhoto: string | null;
  }>({
    firstName: '',
    lastName: '',
    email: '',
    customerType: 1,
    profilePhoto: null,
  });

  const location = useLocation();
  const navigate = useNavigate();
  const isDealer = location.pathname.startsWith('/dealer');

  // 👇 Build nav items with translated labels
  const sellerNav: NavItem[] = [
    { label: t('dashboardLayout.nav.overview'), path: '/dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
    { label: t('dashboardLayout.nav.myListings'), path: '/dashboard/listings', icon: <Car className="w-5 h-5" /> },
    { label: t('dashboardLayout.nav.addVehicle'), path: '/dashboard/add-vehicle', icon: <PlusCircle className="w-5 h-5" /> },
    { label: t('dashboardLayout.nav.leads'), path: '/dashboard/leads', icon: <Users className="w-5 h-5" /> },
    { label: t('dashboardLayout.nav.messages'), path: '/dashboard/messages', icon: <MessageSquare className="w-5 h-5" /> },
    { label: t('dashboardLayout.nav.analytics'), path: '/dashboard/analytics', icon: <BarChart2 className="w-5 h-5" /> },
    { label: t('dashboardLayout.nav.subscription'), path: '/dashboard/subscription', icon: <Tag className="w-5 h-5" /> },
    { label: t('dashboardLayout.nav.payments'), path: '/dashboard/payments', icon: <Wallet className="w-5 h-5" /> },
    { label: t('dashboardLayout.nav.complaints'), path: '/dashboard/complaints', icon: <AlertTriangle className="w-5 h-5" /> },
    { label: t('dashboardLayout.nav.profileSettings'), path: '/dashboard/profile', icon: <Settings className="w-5 h-5" /> },
  ];

  const dealerNav: NavItem[] = [
    { label: t('dashboardLayout.nav.overview'), path: '/dealer', icon: <LayoutDashboard className="w-5 h-5" /> },
    { label: t('dashboardLayout.nav.inventory'), path: '/dealer/inventory', icon: <Car className="w-5 h-5" /> },
    { label: t('dashboardLayout.nav.addVehicle'), path: '/dealer/add-vehicle', icon: <PlusCircle className="w-5 h-5" /> },
    // { label: t('dashboardLayout.nav.teamMembers'), path: '/dealer/team', icon: <Users className="w-5 h-5" /> },
    { label: t('dashboardLayout.nav.leads'), path: '/dealer/leads', icon: <FileText className="w-5 h-5" /> },
    { label: t('dashboardLayout.nav.messages'), path: '/dealer/messages', icon: <MessageSquare className="w-5 h-5" /> },
    { label: t('dashboardLayout.nav.analytics'), path: '/dealer/analytics', icon: <BarChart2 className="w-5 h-5" /> },
    // { label: t('dashboardLayout.nav.branches'), path: '/dealer/branches', icon: <Building2 className="w-5 h-5" /> },
    { label: t('dashboardLayout.nav.subscription'), path: '/dealer/subscription', icon: <CreditCard className="w-5 h-5" /> },
    { label: t('dashboardLayout.nav.complaints'), path: '/dealer/complaints', icon: <AlertTriangle className="w-5 h-5" /> },
    { label: t('dashboardLayout.nav.settings'), path: '/dealer/settings', icon: <Settings className="w-5 h-5" /> },
  ];

  const navItems = isDealer ? dealerNav : sellerNav;
  const dashboardTitle = isDealer
    ? t('dashboardLayout.sidebar.dealer')
    : t('dashboardLayout.sidebar.seller');

  // Load user data from localStorage/cookies
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('customerUser');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        setUserData({
          firstName: parsed.firstName || '',
          lastName: parsed.lastName || '',
          email: parsed.email || '',
          customerType: parsed.customerType || 1,
          profilePhoto: parsed.profilePhoto || null,
        });
      } else {
        const userCookie = Cookies.get('user');
        if (userCookie) {
          const parsed = JSON.parse(userCookie);
          setUserData({
            firstName: parsed.firstName || '',
            lastName: parsed.lastName || '',
            email: parsed.email || '',
            customerType: parsed.customerType || 1,
            profilePhoto: parsed.profilePhoto || null,
          });
        }
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
    }
  }, []);

const handleLogout = () => {
  Cookies.remove('token', cookieOptions);
  Cookies.remove('customerType', cookieOptions);
  Cookies.remove('user', cookieOptions);

  localStorage.removeItem('customerUser');

  setUserData({
    firstName: '',
    lastName: '',
    email: '',
    customerType: 1,
    profilePhoto: null,
  });

  window.dispatchEvent(
    new Event('morrocars-auth-changed')
  );

  navigate('/auth/login', { replace: true });
};

  const getInitials = () => {
    const first = userData.firstName?.charAt(0) || 'U';
    const last = userData.lastName?.charAt(0) || '';
    return (first + last).toUpperCase();
  };

  const getFullName = () => {
    return `${userData.firstName} ${userData.lastName}`.trim() || 'User';
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] flex">
      <ScrollToTop />

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-[#E5E7EB] shadow-lg transform transition-transform duration-300 lg:translate-x-0 lg:static lg:shadow-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="h-20 flex items-center justify-between px-4 border-b border-[#E5E7EB] flex-shrink-0">
            <Link to="/" className="flex items-center min-w-0">
              <img src={logo} alt="MORROCARS" className="w-[170px] h-auto max-h-[60px] object-contain" />
            </Link>
            <button className="lg:hidden flex-shrink-0 ml-2" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5 text-[#2D323A]" />
            </button>
          </div>

          <div className="px-3 py-2 border-b border-[#E5E7EB]">
            <p className="text-xs text-[#717182] uppercase tracking-wider px-3 py-2">{dashboardTitle}</p>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const isActive = item.path === '/dashboard' || item.path === '/dealer'
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    isActive
                      ? 'bg-[#1E6DFF] text-white shadow-md'
                      : 'text-[#2D323A] hover:bg-[#F5F7FA] hover:text-[#1E6DFF]'
                  }`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-[#E5E7EB]">
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[#717182] hover:bg-[#F5F7FA] hover:text-[#EF4444] transition-all">
              <LogOut className="w-5 h-5" />
              <span>{t('dashboardLayout.header.signOut')}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-[#E5E7EB] px-4 lg:px-8 py-4 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6 text-[#2D323A]" />
            </button>
            <div className="relative hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#717182]" />
              <input
                type="text"
                placeholder={t('dashboardLayout.header.searchPlaceholder')}
                className="pl-10 pr-4 py-2 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl text-sm focus:outline-none focus:border-[#1E6DFF] w-64"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <NotificationBell />  

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Profile Dropdown */}
            <div className="relative">
              <button
                className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-[#F5F7FA] transition-colors"
                onClick={() => setProfileOpen(!profileOpen)}
              >
                {userData.profilePhoto ? (
                  <img
                    src={userData.profilePhoto}
                    alt="Profile"
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 bg-[#1E6DFF] rounded-full flex items-center justify-center text-white text-sm font-medium">
                    {getInitials()}
                  </div>
                )}
                <span className="hidden md:block text-sm text-[#2D323A]">{getFullName()}</span>
                <ChevronDown className="w-4 h-4 text-[#717182]" />
              </button>
              {profileOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-[#E5E7EB] rounded-xl shadow-lg py-2 z-50">
                  <Link to="/dashboard/profile" className="flex items-center gap-2 px-4 py-2 text-sm text-[#2D323A] hover:bg-[#F5F7FA]">
                    <User className="w-4 h-4" /> {t('dashboardLayout.header.profile')}
                  </Link>
                  <Link to="/dashboard/subscription" className="flex items-center gap-2 px-4 py-2 text-sm text-[#2D323A] hover:bg-[#F5F7FA]">
                    <Tag className="w-4 h-4" /> {t('dashboardLayout.nav.subscription')}
                  </Link>
                  <hr className="my-1 border-[#E5E7EB]" />
                  <button onClick={handleLogout} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#EF4444] hover:bg-[#F5F7FA] text-left">
                    <LogOut className="w-4 h-4" /> {t('dashboardLayout.header.signOut')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}